import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Mail, RefreshCw, Filter, FileText, Sparkles, MessageSquare, PenLine, Users, Search, Building2, Lightbulb, LayoutGrid } from 'lucide-react';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { ROUTE_PATHS } from '@/lib/index';
import { IMAGES } from '@/assets/images';

export default function About() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  return (
    <Layout>
      <div className="flex-1">

        {/* ── HERO — Foto + Zitat ── */}
        <section className="relative overflow-hidden bg-slate-950">
          <div className="pointer-events-none absolute -top-40 -right-40 h-[500px] w-[500px] rounded-full bg-blue-600/10 blur-3xl" />
          <div className="pointer-events-none absolute bottom-0 -left-20 h-72 w-72 rounded-full bg-indigo-600/8 blur-3xl" />

          <div className="container mx-auto px-4 relative z-10">
            <div className="grid md:grid-cols-2 gap-0 min-h-[540px]">

              {/* Linke Spalte — Text */}
              <div className="flex flex-col justify-center py-20 pr-0 md:pr-12">
                <motion.div
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <div className="inline-flex items-center gap-2 rounded-full border border-blue-500/30 bg-blue-500/10 px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-blue-400 mb-8">
                    Gründer & Entwickler
                  </div>

                  <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-white leading-tight mb-6">
                    Hallo,<br />ich bin Jonas.
                  </h1>

                  <div className="relative pl-5 border-l-2 border-blue-500/60 mt-8">
                    <p className="text-lg md:text-xl text-slate-300 leading-relaxed italic font-light">
                      „Nach über 20 Jahren im Vertrieb habe ich aufgehört zu raten,
                      warum Unternehmen Umsatz verlieren –{' '}
                      <span className="text-white font-semibold not-italic">
                        und angefangen, es messbar zu machen.
                      </span>"
                    </p>
                    <p className="mt-5 text-sm font-semibold text-slate-400 tracking-wide">
                      Jonas Huber · Gründer, Salesforge
                    </p>
                  </div>
                </motion.div>
              </div>

              {/* Rechte Spalte — Foto */}
              <motion.div
                initial={{ opacity: 0, scale: 0.97 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.7, delay: 0.15 }}
                className="relative flex items-end justify-center md:justify-end pt-12 md:pt-0"
              >
                <div className="relative w-full max-w-sm md:max-w-none">
                  <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-3/4 h-32 bg-blue-600/20 blur-2xl rounded-full" />
                  <img
                    src={IMAGES.BILDB_RO_61}
                    alt="Jonas Huber – Gründer von Salesforge"
                    className="relative z-10 w-full object-cover rounded-t-2xl md:rounded-tl-2xl md:rounded-tr-none"
                    style={{ maxHeight: '580px', objectPosition: 'center 15%' }}
                  />
                  <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-slate-950 to-transparent z-20 rounded-b-none" />
                </div>
              </motion.div>

            </div>
          </div>
        </section>

        {/* ── HAUPTTEXT — volle Breite, zwei Spalten ── */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="grid md:grid-cols-2 gap-16 items-start">

                {/* Linke Spalte — persönlicher Text */}
                <div className="space-y-8">

                  <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                    className="text-lg text-foreground/80 leading-relaxed"
                  >
                    In mehr als zwei Jahrzehnten Vertrieb habe ich hunderte Gespräche geführt,
                    Teams aufgebaut, Märkte entwickelt und Unternehmen dabei unterstützt,
                    mehr Umsatz zu generieren.
                  </motion.p>

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.05 }}
                    className="rounded-2xl border border-border bg-muted/40 px-8 py-8"
                  >
                    <p className="text-2xl font-bold text-foreground leading-snug">
                      Die meisten Unternehmen haben kein Vertriebsproblem.
                    </p>
                    <p className="text-2xl font-bold text-primary leading-snug mt-1">
                      Sie haben ein Klarheitsproblem.
                    </p>
                  </motion.div>

                  <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                    className="text-lg text-foreground/80 leading-relaxed"
                  >
                    Oft wird unglaublich viel Energie investiert — in Akquise, Angebote, Marketing
                    oder Neukundengewinnung. Trotzdem bleiben Ergebnisse hinter den Erwartungen zurück.
                    Nicht weil die Menschen schlecht arbeiten, sondern weil niemand genau erkennt,
                    wo Umsatz verloren geht und welche Stellschrauben wirklich den größten Hebel haben.
                  </motion.p>

                  <motion.div
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    className="flex items-center gap-4"
                  >
                    <div className="flex-1 h-px bg-border" />
                    <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground px-2">
                      Deshalb Salesforge
                    </span>
                    <div className="flex-1 h-px bg-border" />
                  </motion.div>

                  <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                    className="text-lg text-foreground/80 leading-relaxed"
                  >
                    Ich wollte ein Werkzeug schaffen, das Unternehmen innerhalb weniger Minuten zeigt,
                    wo sie heute stehen, welche Umsatzpotenziale ungenutzt bleiben und welche
                    Maßnahmen den größten Unterschied machen.
                  </motion.p>

                  <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                    className="text-lg text-foreground/80 leading-relaxed"
                  >
                    Salesforge kombiniert meine Erfahrung aus über zwei Jahrzehnten Vertrieb
                    mit einem strukturierten Analyseansatz. Das Ergebnis ist ein Audit, das
                    schnell auf den Punkt kommt — und Unternehmen dabei hilft, bessere
                    Entscheidungen für ihren Vertrieb zu treffen.
                  </motion.p>

                  {/* Signatur */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                    className="pt-2"
                  >
                    <p className="text-lg text-foreground/80 leading-relaxed mb-8">
                      Ich freue mich darauf, Ihr Unternehmen kennenzulernen.
                    </p>
                    <div className="flex items-center gap-4">
                      <div className="relative w-16 h-16 flex-shrink-0">
                        <img
                          src={IMAGES.BILDB_RO_61}
                          alt="Jonas Huber"
                          className="w-16 h-16 rounded-full object-cover border-2 border-primary/20 shadow-md"
                          style={{ objectPosition: 'center 10%' }}
                        />
                        <div className="absolute -inset-0.5 rounded-full border-2 border-primary/30" />
                      </div>
                      <div>
                        <p className="text-xl font-bold text-foreground">Jonas Huber</p>
                        <p className="text-sm text-muted-foreground">Gründer von Salesforge</p>
                      </div>
                    </div>
                  </motion.div>

                </div>

                {/* Rechte Spalte — Highlights */}
                <div className="space-y-6 md:pt-4">

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                    className="rounded-2xl bg-slate-950 text-white px-8 py-8"
                  >
                    <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-4">Mein Anspruch</p>
                    <p className="text-lg font-semibold text-slate-200 leading-relaxed mb-3">
                      Wenn Sie Zeit investieren, sollen Sie danach mehr Klarheit haben als vorher.
                    </p>
                    <p className="text-sm text-slate-400 leading-relaxed">
                      Denn bessere Entscheidungen entstehen nicht durch mehr Daten —
                      sondern durch die richtigen Erkenntnisse.
                    </p>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.08 }}
                    className="grid grid-cols-2 gap-4"
                  >
                    {[
                      { num: '20+', label: 'Jahre Vertrieb' },
                      { num: 'Ø 11 Min', label: 'bis zur ersten Erkenntnis' },
                      { num: 'B2B', label: 'Fokus' },
                      { num: '100%', label: 'Klarheit statt Aktionismus' },
                    ].map((s, i) => (
                      <div key={i} className="rounded-xl border border-border bg-muted/30 p-5 text-center">
                        <p className="text-2xl font-black text-primary">{s.num}</p>
                        <p className="text-xs text-muted-foreground mt-1 leading-tight">{s.label}</p>
                      </div>
                    ))}
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.12 }}
                    className="rounded-2xl border border-border bg-card px-6 py-6"
                  >
                    <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-5">Was Salesforge nicht ist</p>
                    {['Kein generisches SaaS-Dashboard.', 'Kein Berater-BlaBla.', 'Keine 80-seitigen Präsentationen.'].map((t, i) => (
                      <div key={i} className="flex items-start gap-2.5 mb-3 last:mb-0">
                        <span className="mt-1.5 w-4 h-4 rounded-full border-2 border-destructive/40 flex-shrink-0 flex items-center justify-center">
                          <span className="w-1 h-1 rounded-full bg-destructive/60" />
                        </span>
                        <p className="text-sm text-foreground/70">{t}</p>
                      </div>
                    ))}
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.16 }}
                    className="rounded-2xl border border-primary/20 bg-primary/5 px-6 py-6"
                  >
                    <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-5">Sondern</p>
                    {['Eine ehrliche Analyse.', 'Verständliche Ergebnisse.', 'Konkrete Handlungsempfehlungen.'].map((t, i) => (
                      <div key={i} className="flex items-start gap-2.5 mb-3 last:mb-0">
                        <span className="mt-1.5 w-4 h-4 rounded-full bg-primary/20 flex-shrink-0 flex items-center justify-center">
                          <span className="w-1 h-1 rounded-full bg-primary" />
                        </span>
                        <p className="text-sm font-semibold text-foreground">{t}</p>
                      </div>
                    ))}
                  </motion.div>

                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── Digitales Team ── */}
        <section className="py-20 border-t border-border bg-gradient-to-b from-slate-50/50 to-white">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">

              {/* Header */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="text-center mb-14"
              >
                <div className="inline-flex items-center gap-2 rounded-full border border-violet-200 bg-violet-50 px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-violet-600 mb-5">
                  <Sparkles className="w-3 h-3" />
                  Digitale Assistenz
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4">
                  Unser digitales Team im Hintergrund
                </h2>
                <p className="text-muted-foreground leading-relaxed max-w-2xl mx-auto">
                  Damit Vertrieb nicht nur analysiert, sondern auch im Alltag leichter wird, arbeiten bei Salesforge
                  drei digitale Assistenten mit: Lisa, Markus und Tom. Sie helfen dabei, Prozesse vorzubereiten,
                  Informationen zu sortieren, Kommunikation klarer zu machen und die richtigen Zielkunden zu finden.
                </p>
              </motion.div>

              {/* Drei Cards nebeneinander */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">

                {/* Lisa Card */}
                <motion.div
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0 }}
                  className="relative bg-white border border-border rounded-2xl p-8 shadow-sm overflow-hidden"
                >
                  <div className="pointer-events-none absolute -top-10 -right-10 w-40 h-40 rounded-full bg-blue-50 blur-2xl" />
                  <div className="flex justify-end mb-4">
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 border border-blue-200 px-3 py-1 text-xs font-semibold text-blue-600">
                      <span className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse" />
                      Virtuelle Mitarbeiterin
                    </span>
                  </div>
                  <div className="flex items-center gap-4 mb-6">
                    <div className="relative w-20 h-20 flex-shrink-0">
                      <img
                        src={IMAGES.LISASALESINNENDIENST2_62}
                        alt="Lisa – Digitale Sales Assistenz"
                        className="w-20 h-20 rounded-full object-cover object-top border-2 border-blue-100 shadow-md"
                      />
                      <div className="absolute -inset-0.5 rounded-full border-2 border-blue-200/60" />
                    </div>
                    <div>
                      <p className="text-xl font-bold text-foreground">Lisa</p>
                      <p className="text-sm font-medium text-blue-600">Digitale Sales Assistenz</p>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed mb-5">
                    Lisa unterstützt im Sales Innendienst. Sie hilft dabei, Anfragen zu sortieren, Mailings
                    vorzubereiten, Follow-ups im Blick zu behalten und Interessenten mit den richtigen
                    Informationen zu versorgen.
                  </p>
                  <p className="text-sm font-medium text-foreground mb-6">
                    Kurz gesagt: Lisa sorgt dafür, dass aus Kontakten echte Chancen werden – ohne dass
                    etwas im Postfach untergeht.
                  </p>
                  <div className="w-full h-px bg-border mb-5" />
                  <ul className="space-y-2.5">
                    {[
                      { icon: <Mail className="w-3.5 h-3.5" />, text: 'Mailings vorbereiten' },
                      { icon: <RefreshCw className="w-3.5 h-3.5" />, text: 'Follow-ups strukturieren' },
                      { icon: <Filter className="w-3.5 h-3.5" />, text: 'Anfragen vorsortieren' },
                      { icon: <FileText className="w-3.5 h-3.5" />, text: 'Audit-Ergebnisse aufbereiten' },
                    ].map((item, i) => (
                      <li key={i} className="flex items-center gap-2.5 text-sm text-foreground/80">
                        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-50 flex items-center justify-center text-blue-500">
                          {item.icon}
                        </span>
                        {item.text}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-6 pt-5 border-t border-border">
                    <blockquote className="text-sm text-foreground/60 leading-relaxed italic">
                      &ldquo;Guter Vertrieb entsteht nicht durch Zufall — er entsteht durch saubere
                      Prozesse, klare Daten und ein CRM, das wirklich gelebt wird.&rdquo;
                    </blockquote>
                  </div>
                </motion.div>

                {/* Markus Card */}
                <motion.div
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                  className="relative bg-white border border-border rounded-2xl p-8 shadow-sm overflow-hidden"
                >
                  <div className="pointer-events-none absolute -top-10 -right-10 w-40 h-40 rounded-full bg-violet-50 blur-2xl" />
                  <div className="flex justify-end mb-4">
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-violet-50 border border-violet-200 px-3 py-1 text-xs font-semibold text-violet-600">
                      <span className="w-1.5 h-1.5 rounded-full bg-violet-400 animate-pulse" />
                      Virtueller Mitarbeiter
                    </span>
                  </div>
                  <div className="flex items-center gap-4 mb-6">
                    <div className="relative w-20 h-20 flex-shrink-0">
                      <img
                        src={IMAGES.MARKUSSTUDENTISCHE_AUSHILFE_UND_EMAIL_MAGIER_63}
                        alt="Markus – Der E-Mail-Magier"
                        className="w-20 h-20 rounded-full object-cover object-top border-2 border-violet-100 shadow-md"
                      />
                      <div className="absolute -inset-0.5 rounded-full border-2 border-violet-200/60" />
                    </div>
                    <div>
                      <p className="text-xl font-bold text-foreground">Markus</p>
                      <p className="text-sm font-medium text-violet-600">Der E-Mail-Magier</p>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed mb-5">
                    Markus ist unser digitaler Spezialist für klare Worte. Er hilft dabei, E-Mails besser
                    zu formulieren, Botschaften auf den Punkt zu bringen und aus trockener Kommunikation
                    echte Gesprächsanlässe zu machen.
                  </p>
                  <p className="text-sm font-medium text-foreground mb-6">
                    Kurz gesagt: Markus macht aus „Wir melden uns mal" eine Nachricht, auf die man auch
                    wirklich antworten möchte.
                  </p>
                  <div className="w-full h-px bg-border mb-5" />
                  <ul className="space-y-2.5">
                    {[
                      { icon: <Mail className="w-3.5 h-3.5" />, text: 'E-Mail-Texte verbessern' },
                      { icon: <PenLine className="w-3.5 h-3.5" />, text: 'Betreffzeilen schärfen' },
                      { icon: <MessageSquare className="w-3.5 h-3.5" />, text: 'Follow-up-Vorlagen erstellen' },
                      { icon: <Users className="w-3.5 h-3.5" />, text: 'Kommunikation menschlicher machen' },
                    ].map((item, i) => (
                      <li key={i} className="flex items-center gap-2.5 text-sm text-foreground/80">
                        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-violet-50 flex items-center justify-center text-violet-500">
                          {item.icon}
                        </span>
                        {item.text}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-6 pt-5 border-t border-border">
                    <blockquote className="text-sm text-foreground/60 leading-relaxed italic">
                      &ldquo;Jede E-Mail ist eine Chance — wenn sie zum richtigen Zeitpunkt,
                      mit der richtigen Botschaft ankommt.&rdquo;
                    </blockquote>
                  </div>
                </motion.div>

                {/* Tom Card */}
                <motion.div
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  className="relative bg-white border border-border rounded-2xl p-8 shadow-sm overflow-hidden"
                >
                  <div className="pointer-events-none absolute -top-10 -right-10 w-40 h-40 rounded-full bg-emerald-50 blur-2xl" />
                  <div className="flex justify-end mb-4">
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 border border-emerald-200 px-3 py-1 text-xs font-semibold text-emerald-600">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                      Virtueller Mitarbeiter
                    </span>
                  </div>
                  <div className="flex items-center gap-4 mb-6">
                    <div className="relative w-20 h-20 flex-shrink-0">
                      <img
                        src={IMAGES.TOM_64}
                        alt="Tom – Der Lead-Spürhund"
                        className="w-20 h-20 rounded-full object-cover object-top border-2 border-emerald-100 shadow-md"
                      />
                      <div className="absolute -inset-0.5 rounded-full border-2 border-emerald-200/60" />
                    </div>
                    <div>
                      <p className="text-xl font-bold text-foreground">Tom</p>
                      <p className="text-sm font-medium text-emerald-600">Der Lead-Spürhund</p>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed mb-5">
                    Tom ist unser digitaler Assistent für die erste Vertriebsrecherche. Er hilft dabei,
                    passende Zielkunden zu identifizieren, Branchen sauber zu clustern und spannende
                    Unternehmen für die Akquise vorzubereiten.
                  </p>
                  <p className="text-sm font-medium text-foreground mb-6">
                    Kurz gesagt: Tom findet nicht einfach Kontakte – er sucht nach den richtigen
                    Ansatzpunkten für ein gutes Gespräch.
                  </p>
                  <div className="w-full h-px bg-border mb-5" />
                  <ul className="space-y-2.5">
                    {[
                      { icon: <Search className="w-3.5 h-3.5" />, text: 'Zielkunden recherchieren' },
                      { icon: <LayoutGrid className="w-3.5 h-3.5" />, text: 'Branchen clustern' },
                      { icon: <Building2 className="w-3.5 h-3.5" />, text: 'Firmenprofile vorbereiten' },
                      { icon: <Lightbulb className="w-3.5 h-3.5" />, text: 'Gesprächsanlässe erkennen' },
                    ].map((item, i) => (
                      <li key={i} className="flex items-center gap-2.5 text-sm text-foreground/80">
                        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-emerald-50 flex items-center justify-center text-emerald-500">
                          {item.icon}
                        </span>
                        {item.text}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-6 pt-5 border-t border-border">
                    <blockquote className="text-sm text-foreground/60 leading-relaxed italic">
                      &ldquo;Der erste Eindruck entscheidet — aber nur, wenn man vorher weiß,
                      mit wem man spricht und warum gerade jetzt.&rdquo;
                    </blockquote>
                  </div>
                </motion.div>

              </div>

              {/* Hinweis */}
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="mt-10 text-center"
              >
                <p className="text-xs text-muted-foreground/70 leading-relaxed max-w-xl mx-auto">
                  💡 Lisa, Markus und Tom sind digitale Assistenten. Sie ersetzen kein persönliches Gespräch,
                  sondern helfen uns dabei, schneller, klarer und strukturierter zu arbeiten.
                </p>
              </motion.div>

            </div>
          </div>
        </section>

        {/* ── CTA ── */}
        <section className="py-16 bg-muted/30 border-t border-border">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="max-w-2xl mx-auto text-center"
            >
              <h2 className="text-2xl md:text-3xl font-bold mb-4">
                Bereit für Klarheit in Ihrem Vertrieb?
              </h2>
              <p className="text-muted-foreground mb-8 leading-relaxed">
                Starten Sie jetzt das kostenlose Audit — und sehen Sie in unter 10 Minuten,
                wo Ihr Vertrieb heute steht.
              </p>
              <Button asChild size="lg" className="gap-2 shadow-lg hover:shadow-xl transition-all duration-200">
                <Link to={ROUTE_PATHS.AUDIT}>
                  Audit starten
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </Button>
            </motion.div>
          </div>
        </section>

      </div>
    </Layout>
  );
}
